import { Grid, Autocomplete, Stack, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState, useRef, SyntheticEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  getActiveQuestions,
  saveUserInput,
  updateWorkspace,
  updateUsersCompany,
} from 'wisestep-api';
import {
  Spinner,
  WsTypography,
  WsTypoVariants,
  WsButton,
  Layout,
  IUserDTO,
  ControlledTextInput,
  WsRadioGroup,
  WsRadioObj,
  Label,
  IUsersOrganization,
} from 'wisestep-base';

import { AuthLayout } from '../../../../components';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

const StyledFooter = styled(Grid)(() => ({
  position: 'relative',
  bottom: '0px',
  textAlign: 'center',
  left: 0,
  right: 0,
}));

interface IQuestion {
  questionId: string;
  answers: string[];
  questionValue: string;
  status: boolean;
  questionCategory: string;
  allowOtherButton: boolean;
  createdBy: string;
  updatedBy: string | null;
  createdOn: string | null;
  updatedOn: string | null;
}

const OnBoardingQuestions = () => {
  const methods = useForm();

  const { watch } = methods;

  const history = useNavigate();

  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const workspace = useWorkspaceContext();

  const activeQuestions = useRef<IQuestion[]>([]);

  const [currentQuestion, setCurrentQuestion] = useState<IQuestion | null>(null);

  const [selectedOption, setSelectedOption] = useState<string[] | null>(null);

  const [questionId, setQuestionId] = useState<number>(0);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const [defaultProps, setDefaultProps] = useState<{ options: string[] }>({
    options: [],
  });

  const configureQuestions = useCallback((text: string) => {
    const options: IQuestion = activeQuestions.current.filter(
      (questions: IQuestion) => questions.questionValue === text
    )[0];
    setCurrentQuestion(options);
    setDefaultProps({ options: options.answers });
  }, []);

  const getQuestionOptions = useCallback(
    async (id: number) => {
      if (id <= 5) {
        switch (id) {
          case 1:
            configureQuestions('How many employees are there in company');
            break;
          case 2:
            configureQuestions('What is your role in the organisation?');
            break;
          case 3:
            configureQuestions('How do you plan to use Wisestep?');
            break;
          case 4:
            configureQuestions('In which geographies do you operate/recruit ?');
            break;
          case 5:
            setCurrentQuestion({
              questionId: 'e768353e-5939-11ee-8c99-0242ac120002',
              answers: [],
              questionValue: 'Where did you first hear about Wisestep?',
              status: true,
              questionCategory: 'PAGE-1',
              allowOtherButton: true,
              createdBy: 'admin',
              updatedBy: null,
              createdOn: '2022-10-19T19:18:51.254Z',
              updatedOn: null,
            });
            break;
        }
      } else {
        if (workspace.setWorkspace) {
          const tempWorkspace = { ...workspace };
          tempWorkspace.onBoardingQuestionAnswered = true;
          workspace.setWorkspace(tempWorkspace);
          tempWorkspace.workspacePlan = null;
          await updateWorkspace(tempWorkspace);
          history('/home');
        }
      }
    },
    [configureQuestions, history, workspace]
  );
  const apiCallActiveQuestions = useCallback(async () => {
    if (isEmpty(activeQuestions.current)) {
      const getActiveQuestionsData = await getActiveQuestions();
      activeQuestions.current = getActiveQuestionsData.data;
      getQuestionOptions(1);
    }
  }, [activeQuestions, getQuestionOptions]);

  useEffect(() => {
    workspace.onBoardingQuestionAnswered ? history('/') : apiCallActiveQuestions();
  }, [apiCallActiveQuestions, history, workspace.onBoardingQuestionAnswered]);

  const organization = watch('companyName');

  const getNextQuestion = async () => {
    if (currentQuestion && (selectedOption || sourceOptions)) {
      saveAnswer(currentQuestion, selectedOption);
      const id = questionId;
      getQuestionOptions(id + 1);
      setQuestionId(id + 1);
      setSelectedOption(null);
    } else {
      if (organization) {
        const tempWorkspace = { ...workspace };
        const tempUsersOrg: IUsersOrganization = {};
        tempUsersOrg.organization = organization;
        tempUsersOrg.email = loggedInUser.email;
        tempWorkspace.organization = organization;
        tempWorkspace.setWorkspace && tempWorkspace.setWorkspace(tempWorkspace);
        tempWorkspace.workspacePlan = null;
        await updateUsersCompany(tempUsersOrg);
        await updateWorkspace(tempWorkspace);
        const id = questionId;
        setQuestionId(id + 1);
      } else {
        setNotification(warning('Please enter the Company Name'));
      }
    }
  };

  const saveAnswer = async (currentQuestion: IQuestion, options: string[] | null) => {
    if ((options || sourceOptions) && loggedInUser.email) {
      let selectedAnswer = options ? options : [];
      const isSourceGatheringQue =
        currentQuestion.questionValue === 'Where did you first hear about Wisestep?';
      if (isSourceGatheringQue) {
        if (sourceOptions === 'others' && !isEmpty(otherWisestepSource)) {
          selectedAnswer = [otherWisestepSource];
        } else {
          selectedAnswer = sourceOptions ? [sourceOptions] : [];
        }
      }
      const questionAnswered = {
        answers: selectedAnswer,
        questionValue: currentQuestion.questionValue,
        questionId: isSourceGatheringQue
          ? currentQuestion.questionId
          : 'e768353e-5939-11ee-8c99-0242ac120002',
        userId: loggedInUser.email,
      };
      await saveUserInput(questionAnswered);
    }
  };

  const handleChangeSelectedOption = (
    event: SyntheticEvent<Element, Event>,
    newValue: string | string[] | null
  ) => {
    if (Array.isArray(newValue)) {
      newValue && setSelectedOption(newValue);
    } else {
      newValue && setSelectedOption([newValue]);
    }
  };

  const onBoardingOptions = (multiple: boolean) => (
    <Autocomplete
      sx={{ display: 'inline' }}
      {...defaultProps}
      id="clear-on-escape"
      clearOnEscape
      onChange={handleChangeSelectedOption}
      renderInput={(params) => (
        <TextField
          sx={{ width: '200px' }}
          {...params}
          placeholder="choose option"
          variant="standard"
        />
      )}
      multiple={multiple}
    />
  );

  const renderQuestions = (text1: string, text2: string | null, multiple: boolean) => (
    <>
      {text2 ? (
        <Grid container justifyContent="center" direction="row" pt={3}>
          <Grid item xs={3}>
            <WsTypography variant={WsTypoVariants.TITLE}>{text1}</WsTypography>
          </Grid>
          {onBoardingOptions(multiple)}
          <Grid item xs={2} pl={1}>
            <WsTypography variant={WsTypoVariants.TITLE}>{text2}</WsTypography>
          </Grid>
        </Grid>
      ) : (
        <Grid container justifyContent="center" pt={3} direction="row">
          <Grid item xs={3}>
            <WsTypography variant={WsTypoVariants.TITLE}>{text1}</WsTypography>
          </Grid>
          <Grid item xs={4}>
            {onBoardingOptions(multiple)}
          </Grid>
        </Grid>
      )}
    </>
  );
  const sourceOptions = watch('sourceOptions');
  const otherWisestepSource = watch('otherWisestepSource');
  const onboardedStatusOptions: WsRadioObj[] = [
    {
      label: 'TV Commercials',
      value: 'tvCommercials',
    },
    {
      label: 'Superbowl',
      value: 'superbowl',
    },
    {
      label: 'Social Media',
      value: 'socialMedia',
    },
    {
      label: 'Google Search',
      value: 'googleSearch',
    },
    {
      label: 'Word of Mouth',
      value: 'wordOfMouth',
    },
    {
      label: 'Blog',
      value: 'blog',
    },
    {
      label: 'Airport',
      value: 'airport',
    },
    {
      label: 'Call/Email',
      value: 'callOrEmail',
    },
    {
      label: 'Horoscope',
      value: 'horoscope',
    },
    { label: 'Others', value: 'others' },
  ];

  return (
    <AuthLayout>
      {questionId <= 5 ? (
        <FormProvider {...methods}>
          <div id="onboarding">
            <Layout>
              <Grid container>
                <Grid item xs={12} textAlign="center" pb={2}>
                  <h2>Tell us about yourself</h2>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    This will help us provide you with a better experience
                  </WsTypography>
                </Grid>
                {questionId === 0 && (
                  <>
                    <Grid container justifyContent="center" direction="row" pt={3}>
                      <Grid item xs={4}>
                        <ControlledTextInput
                          name="companyName"
                          labelText="Company Name *"
                          placeholder="Eg, Infosys"
                          variant="outlined"
                          required
                          autoComplete="companyName"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="center" mt={4}>
                          <WsButton
                            id="continue"
                            size="large"
                            onClick={getNextQuestion}
                            variant="contained"
                          >
                            Continue
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </>
                )}
                {questionId === 1 && renderQuestions('My company has', 'employees.', false)}
                {questionId === 2 && renderQuestions('I work here as an', null, false)}
                {questionId === 3 && renderQuestions('I plan to use Wisestep for', null, true)}
                {questionId === 4 &&
                  renderQuestions('My company hires candidates from', null, true)}
                {questionId === 5 && (
                  <>
                    <Grid container justifyContent="center" direction="row" pt={3}>
                      <Grid item xs={6}>
                        <Grid item pb={1.5}>
                          <Label>Where did you first hear about Wisestep?</Label>
                        </Grid>
                        <WsRadioGroup
                          name="sourceOptions"
                          selectArray={onboardedStatusOptions}
                          row={false}
                        />
                        {sourceOptions === 'others' && (
                          <>
                            <WsTypography>Enter other source</WsTypography>
                            <ControlledTextInput
                              name="otherWisestepSource"
                              labelText=""
                              inputProps={{}}
                              size="small"
                              required
                            />
                          </>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="center" mt={4}>
                          <WsButton
                            id="continue"
                            size="large"
                            onClick={getNextQuestion}
                            variant="contained"
                          >
                            Continue
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Layout>
            <StyledFooter>
              <Stack spacing={3} direction="row">
                {selectedOption ? (
                  <WsButton
                    id="continue"
                    size="large"
                    onClick={getNextQuestion}
                    variant="contained"
                  >
                    Continue
                  </WsButton>
                ) : (
                  <>
                    {questionId !== 0 && questionId !== 5 && (
                      <WsButton id="continue" size="large" disabled={true} variant="contained">
                        Continue
                      </WsButton>
                    )}
                  </>
                )}
              </Stack>
            </StyledFooter>
          </div>
        </FormProvider>
      ) : (
        <Spinner />
      )}
    </AuthLayout>
  );
};
export default OnBoardingQuestions;
